/** @typedef {'en' | 'sr'} Locale */

/** @type {Record<Locale, Record<string, string | Record<string, string>>>} */
export const translations = {
  en: {
    nav: {
      home: 'Home',
      meals: 'Meals',
      workouts: 'Workouts',
      reports: 'Reports',
      templates: 'Templates',
      profile: 'Profile',
      admin: 'Admin Console',
      logout: 'Logout',
      fitnessCoach: 'Fitness Coach',
      logoutSuccess: 'Logged out successfully',
    },
    lang: {
      label: 'Language',
      en: 'EN',
      sr: 'RS',
    },
    common: {
      loading: 'Loading...',
      saved: 'Saved!',
      saveChanges: 'Save Changes',
      selectCustomer: 'Select Customer:',
      chooseCustomer: 'Choose a customer...',
      customer: 'Customer:',
      pleaseSelectCustomer: 'Please select a customer to view and edit their plan.',
    },
    login: {
      username: 'Username',
      password: 'Password',
      rememberMe: 'Remember me',
      signIn: 'Sign In',
      signingIn: 'Signing in...',
      placeholderUser: 'Enter your username',
      placeholderPass: 'Enter your password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      loginSuccess: 'Login successful!',
    },
    hrana: {
      title: 'Meal Plans',
      downloadImage: 'Download Meal Plan Image',
      preparingImage: 'Preparing image...',
      mealsPerDay: 'Number of meals per day:',
      meal: 'Meal',
      food: 'Food',
      grams: 'Grams',
      supplements: 'Supplements',
      amount: 'Amount',
      time: 'Time',
      nutritionTotals: 'Nutrition Totals',
      protein: 'Protein:',
      carbs: 'Carbs:',
      fats: 'Fats:',
      calories: 'Calories:',
      selectFood: 'Select food...',
      selectSupplement: 'Select supplement...',
      addFoodFirst: 'Add food first',
      pieces: 'pieces',
      gramsUnit: 'grams',
      addSupplementFirst: 'Add supplement first',
      timePlaceholder: 'e.g. Morning / before training',
      noFoodAssigned: 'No food assigned',
      removeRow: 'Remove row',
      selectMealSlot: 'Select meal:',
      chooseMealSlot: 'Choose a meal...',
      dailySupplementsTitle: 'Daily supplements',
      addSupplementRow: 'Add supplement row',
      dose: 'Dosage',
      addVariation: 'Add variation',
      removeVariation: 'Delete',
      variation: 'Variation',
      tipsTitle: 'Tips & Tricks',
      addTip: 'Add tip',
      tipTitlePlaceholder: 'Tip title...',
      tipBodyPlaceholder: 'Write your tip here...',
      deleteTip: 'Delete',
      suppTemplateTitle: 'Save supplements as template',
      suppTemplateEmpty: 'Add at least one supplement first',
      suppTemplateReplaceConfirm: 'Replace the current supplements with this template?',
    },
    templates: {
      title: 'Templates',
      hint: 'Open Meals or Workouts, build a plan, then click "Save as template". Templates can then be loaded from Meals or Workouts pages.',
      mealTemplates: 'Meal Templates',
      workoutTemplates: 'Workout Templates',
      saveAsTemplate: 'Save as template',
      templateName: 'Template name',
      templateNamePlaceholder: 'e.g. Cutting diet week 1',
      saveTemplate: 'Save Template',
      loadTemplate: 'Load template',
      selectTemplate: 'Select a template...',
      noTemplates: 'No templates yet.',
      deleteTemplate: 'Delete',
      confirmDelete: 'Delete this template?',
      savedSuccess: 'Template saved!',
      deletedSuccess: 'Template deleted.',
      loadedSuccess: 'Template loaded!',
      mealsLabel: 'meals/day',
      workoutsLabel: 'workouts/week',
      tabMeals: 'Meal Templates',
      tabWorkouts: 'Workout Templates',
      tabSupplements: 'Supplement Templates',
      supplementTemplates: 'Supplement Templates',
      supplementsLabel: 'supplements',
      cancel: 'Cancel',
      selectMeals: 'Select meals to include',
      allMeals: 'All meals',
    },
    trening: {
      title: 'Workout Plans',
      workoutsPerWeek: 'Number of workouts per week:',
      downloadImage: 'Download Workout Plan Image',
      preparingImage: 'Preparing image...',
      legendTitle: 'Legend',
      workout: 'Workout',
      exercises: 'Exercises',
      sets: 'Sets',
      reps: 'Reps',
      ptAdvice: 'PT Advice',
      selectExercise: 'Select exercise...',
      addExerciseFirst: 'Add exercise first',
      ptAdvicePlaceholder: 'Add PT advice or notes for this workout',
      noExercises: 'No exercises assigned',
      noAdvice: 'No PT advice yet',
      removeExercise: 'Remove exercise',
      selectWorkoutSlot: 'Select workout:',
      chooseWorkoutSlot: 'Choose a workout...',
      addVariation: 'Add variation',
      removeVariation: 'Delete',
      variation: 'Variation',
      warmupTitle: 'Warmup exercises',
      warmupExercise: 'Exercise',
      warmupAdvicePlaceholder: 'Coach notes for this exercise',
      rest: 'Rest',
      restPlaceholder: 'e.g. 90s',
      restSuffix: 'rest',
      exerciseNote: 'Coach note',
      exerciseNotePlaceholder: 'e.g. 20 kg, slow on the way down...',
      addWarmupExercise: 'Add warmup exercise',
      exerciseTechnique: 'Set type',
      technique: {
        none: '—',
        superset: 'Superset',
        giantSet: 'Giant set',
        dropSet: 'Drop set',
        circuit: 'Circuit',
      },
      legend: {
        rm: 'RM — Max rep (e.g. 1RM = heaviest weight for one rep)',
        rmVariants: '1RM / 3RM / 5RM — Max weight for 1, 3, or 5 reps',
        pct1rm: '%1RM — Percentage of your one-rep max',
        amrap: 'AMRAP — As many reps as possible',
        rpe: 'RPE — Rate of perceived exertion (1–10)',
        rir: 'RIR — Reps in reserve (reps left before failure)',
        tut: 'TUT — Time under tension',
        rom: 'ROM — Range of motion',
        dropset: 'DS / DROPSET — Drop set (reduce weight after failure, no rest)',
        bw: 'BW — Body weight',
        superset: 'SS / SUPERSET — Two exercises back-to-back, no rest',
        setTypeColumn:
          'Set type: use the same letter for exercises done as a superset or giant set (e.g. Superset · A on both). Drop set = reduce weight with no rest between parts.',
      },
      tipsTitle: 'Tips & Tricks',
      addTip: 'Add tip',
      tipTitlePlaceholder: 'Tip title...',
      tipBodyPlaceholder: 'Write your tip here...',
      deleteTip: 'Delete',
    },
  },
  sr: {
    nav: {
      home: 'Početna',
      meals: 'Ishrana',
      workouts: 'Trening',
      reports: 'Izveštaji',
      templates: 'Šabloni',
      profile: 'Profil',
      admin: 'Admin',
      logout: 'Odjava',
      fitnessCoach: 'Fitness trener',
      logoutSuccess: 'Uspešno ste se odjavili',
    },
    lang: {
      label: 'Jezik',
      en: 'EN',
      sr: 'RS',
    },
    common: {
      loading: 'Učitavanje...',
      saved: 'Sačuvano!',
      saveChanges: 'Sačuvaj izmene',
      selectCustomer: 'Izaberi klijenta:',
      chooseCustomer: 'Izaberi klijenta...',
      customer: 'Klijent:',
      pleaseSelectCustomer: 'Izaberi klijenta da vidiš i menjaš plan.',
    },
    login: {
      username: 'Korisničko ime',
      password: 'Lozinka',
      rememberMe: 'Zapamti me',
      signIn: 'Prijava',
      signingIn: 'Prijava...',
      placeholderUser: 'Unesi korisničko ime',
      placeholderPass: 'Unesi lozinku',
      showPassword: 'Prikaži lozinku',
      hidePassword: 'Sakrij lozinku',
      loginSuccess: 'Uspešna prijava!',
    },
    hrana: {
      title: 'Plan ishrane',
      downloadImage: 'Preuzmi sliku plana obroka',
      preparingImage: 'Priprema slike...',
      mealsPerDay: 'Broj obroka dnevno:',
      meal: 'Obrok',
      food: 'Hrana',
      grams: 'Grami',
      supplements: 'Suplementi',
      amount: 'Količina',
      time: 'Vreme',
      nutritionTotals: 'Ukupno',
      protein: 'Proteini:',
      carbs: 'Ugljeni hidrati:',
      fats: 'Masti:',
      calories: 'Kalorije:',
      selectFood: 'Izaberi hranu...',
      selectSupplement: 'Izaberi suplement...',
      addFoodFirst: 'Prvo dodaj hranu',
      pieces: 'kom',
      gramsUnit: 'g',
      addSupplementFirst: 'Prvo dodaj suplement',
      timePlaceholder: 'npr. Ujutru / pre treninga',
      noFoodAssigned: 'Nije dodeljena hrana',
      removeRow: 'Ukloni red',
      selectMealSlot: 'Izaberi obrok:',
      chooseMealSlot: 'Izaberi obrok...',
      dailySupplementsTitle: 'Dnevni suplementi',
      addSupplementRow: 'Dodaj red suplementa',
      dose: 'Doza',
      addVariation: 'Dodaj varijantu',
      removeVariation: 'Obriši',
      variation: 'Varijanta',
      tipsTitle: 'Saveti',
      addTip: 'Dodaj savet',
      tipTitlePlaceholder: 'Naslov saveta...',
      tipBodyPlaceholder: 'Upiši savet ovde...',
      deleteTip: 'Obriši',
      suppTemplateTitle: 'Sačuvaj suplemente kao šablon',
      suppTemplateEmpty: 'Prvo dodaj bar jedan suplement',
      suppTemplateReplaceConfirm: 'Zameniti trenutne suplemente ovim šablonom?',
    },
    trening: {
      title: 'Plan treninga',
      workoutsPerWeek: 'Broj treninga nedeljno:',
      downloadImage: 'Preuzmi sliku plana treninga',
      preparingImage: 'Priprema slike...',
      legendTitle: 'Legenda',
      workout: 'Trening',
      exercises: 'Vežbe',
      sets: 'Serije',
      reps: 'Ponavljanja',
      ptAdvice: 'Saveti trenera',
      selectExercise: 'Izaberi vežbu...',
      addExerciseFirst: 'Prvo dodaj vežbu',
      ptAdvicePlaceholder: 'Saveti ili napomene za ovaj trening',
      noExercises: 'Nema dodeljenih vežbi',
      noAdvice: 'Još nema saveta trenera',
      removeExercise: 'Ukloni vežbu',
      selectWorkoutSlot: 'Izaberi trening:',
      chooseWorkoutSlot: 'Izaberi trening...',
      addVariation: 'Dodaj varijantu',
      removeVariation: 'Obriši',
      variation: 'Varijanta',
      warmupTitle: 'Zagrevne vežbe',
      warmupExercise: 'Vežba',
      warmupAdvicePlaceholder: 'Saveti trenera za ovu vežbu',
      rest: 'Odmor',
      restPlaceholder: 'npr. 90s',
      restSuffix: 'odmor',
      exerciseNote: 'Napomena trenera',
      exerciseNotePlaceholder: 'npr. 20 kg, polako u spuštanju...',
      addWarmupExercise: 'Dodaj zagrevnu vežbu',
      exerciseTechnique: 'Tip serije',
      technique: {
        none: '—',
        superset: 'Superset',
        giantSet: 'Giant set',
        dropSet: 'Drop set',
        circuit: 'Krug',
      },
      legend: {
        rm: 'RM — Maksimalno ponavljanje (npr. 1RM = najveća težina za jedno ponavljanje)',
        rmVariants: '1RM / 3RM / 5RM — Maksimalna težina za 1, 3 ili 5 ponavljanja',
        pct1rm: '%1RM — Procenat maksimalne težine za jedno ponavljanje',
        amrap: 'AMRAP — Što više ponavljanja možeš',
        rpe: 'RPE — Subjektivni osećaj napora (skala 1–10)',
        rir: 'RIR — Ponavljanja u rezervi (koliko ponavljanja je preostalo do otkaza)',
        tut: 'TUT — Vreme pod napetošću mišića (Time Under Tension)',
        rom: 'ROM — Opseg pokreta (Range of Motion)',
        dropset: 'DS / DROPSET — Drop set (smanjivanje težine nakon otkaza, bez odmora)',
        bw: 'BW — Težina sopstvenog tela',
        superset: 'SS / SUPERSET — Superset (dve vežbe zaredom, bez odmora)',
        setTypeColumn:
          'Tip serije: ista slova (npr. Superset · A) na dva reda znači da se vežbe rade u supersetu ili giant setu. Drop set = smanji težinu bez odmora između delova.',
      },
      tipsTitle: 'Saveti',
      addTip: 'Dodaj savet',
      tipTitlePlaceholder: 'Naslov saveta...',
      tipBodyPlaceholder: 'Upiši savet ovde...',
      deleteTip: 'Obriši',
    },
    templates: {
      title: 'Šabloni',
      hint: 'Otvori Ishranu ili Trening, napravi plan, zatim klikni "Sačuvaj kao šablon". Šabloni se mogu učitati sa strana Ishrane ili Treninga.',
      mealTemplates: 'Šabloni ishrane',
      workoutTemplates: 'Šabloni treninga',
      saveAsTemplate: 'Sačuvaj kao šablon',
      templateName: 'Naziv šablona',
      templateNamePlaceholder: 'npr. Dijeta za rezanje nedelja 1',
      saveTemplate: 'Sačuvaj šablon',
      loadTemplate: 'Učitaj šablon',
      selectTemplate: 'Izaberi šablon...',
      noTemplates: 'Nema šablona.',
      deleteTemplate: 'Obriši',
      confirmDelete: 'Obrisati ovaj šablon?',
      savedSuccess: 'Šablon sačuvan!',
      deletedSuccess: 'Šablon obrisan.',
      loadedSuccess: 'Šablon učitan!',
      mealsLabel: 'obroka/dan',
      workoutsLabel: 'treninga/ned.',
      tabMeals: 'Šabloni ishrane',
      tabWorkouts: 'Šabloni treninga',
      tabSupplements: 'Šabloni suplemenata',
      supplementTemplates: 'Šabloni suplemenata',
      supplementsLabel: 'suplemenata',
      cancel: 'Otkaži',
      selectMeals: 'Izaberi obroke za uključivanje',
      allMeals: 'Svi obroci',
    },
  },
};

/**
 * @param {Locale} locale
 * @param {string} path dot-separated, e.g. 'nav.home'
 */
export function t(locale, path) {
  const parts = path.split('.');
  /** @type {unknown} */
  let cur = translations[locale] || translations.en;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in cur) {
      cur = cur[p];
    } else {
      cur = translations.en;
      for (const q of parts) {
        if (cur && typeof cur === 'object' && q in cur) cur = cur[q];
        else return path;
      }
      return typeof cur === 'string' ? cur : path;
    }
  }
  return typeof cur === 'string' ? cur : path;
}
